import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  VentureDocumentListCreateDocument,
  VentureDocumentListDeleteDocument,
  VentureDocumentListGetByIdDocument,
  VentureDocumentListPaginatedDocument,
  VentureDocumentListListDocument,
  VentureDocumentListUpdateDocument,
} from "api/generated";
import { Optional } from "config";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

import {
  DocumentListByIdResponse,
  ICreateDocumentListArgs,
  IDocumentListArgs,
  IDocumentListModel,
  IDocumentListResponse,
  IDocumentListUpdateArgs,
  IDocumentModel,
} from "api/services/library/document-list";

export async function getVentureDocumentListPaginated(
  variables: IDocumentListArgs
): Promise<IDocumentListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureDocumentListPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureDocumentListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createVentureDocumentList(
  variables: ICreateDocumentListArgs
): Promise<Optional<Pick<IDocumentListModel, "documentListId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureDocumentListCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all document list queries so they refetch with the new item
    executeEviction(client, {
      typename: "VentureDocumentList",
      fieldNames: ["ventureDocumentListPaginated", "ventureDocumentListList"],
    });

    return res?.data?.ventureDocumentListCreate;
  } catch (error) {
    throw error;
  }
}

export async function getVentureDocumentList(
  documentListId: number
): Promise<DocumentListByIdResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureDocumentListGetByIdDocument,
      variables: {
        documentListId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureDocumentListGetById;
  } catch (error) {
    throw error;
  }
}

export async function updateVentureDocumentList(
  variables: IDocumentListUpdateArgs
): Promise<Optional<Pick<IDocumentListModel, "documentListId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureDocumentListUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "VentureDocumentList",
      id: variables.documentListId.toString(),
      fieldNames: [
        "ventureDocumentListPaginated",
        "ventureDocumentListList",
        "ventureDocumentListGetById",
      ],
    });

    return res?.data?.ventureDocumentListUpdate;
  } catch (error) {
    throw error;
  }
}

export async function deleteVentureDocumentList(
  documentListId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureDocumentListDeleteDocument,
      variables: {
        documentListId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "VentureDocumentList",
      id: documentListId.toString(),
      fieldNames: [
        "ventureDocumentListPaginated",
        "ventureDocumentListList",
        "ventureDocumentListGetById",
      ],
    });

    return Boolean(res?.data?.ventureDocumentListDelete);
  } catch (error) {
    throw error;
  }
}

export async function getVentureDocumentLists(): Promise<
  (Pick<IDocumentListModel, "name" | "documentListId"> & {
    documents: Pick<IDocumentModel, "documentId" | "name">[];
  })[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureDocumentListListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureDocumentListList ?? [];
  } catch (error) {
    throw error;
  }
}
